import clsx from 'clsx';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import React, { useEffect } from 'react';
import { useLocalStorage, useSearchParam } from 'react-use';
import * as Yup from 'yup';
import { useCreateAccountRegistration } from '../../hooks/useCreateAccountRegistration';

const billingTypes = ['monthly', 'annual'] as const;
const planTypes = ['essentials', 'pro'] as const;

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .required('Email address is required')
    .email('Invalid email address'),
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  password: Yup.string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters'),
});

type Props = {
  onSuccess: () => void;
};

const GetStartedForm = ({ onSuccess }: Props) => {
  const [localStorageCoupon, setLocalStorageCoupon, removeLocalStorageCoupon] =
    useLocalStorage<string | null>('coupon');
  const searchParamCoupon = useSearchParam('coupon') ?? null;
  const couponCode = searchParamCoupon ?? localStorageCoupon ?? null;
  const planDuration = useSearchParam('billing') ?? 'monthly';
  const planName = useSearchParam('plan') ?? 'essentials';

  const {
    mutate: registerAccount,
    isLoading: isSubmitting,
    isError,
    error,
  } = useCreateAccountRegistration();

  const initialValues = {
    email: '',
    firstName: '',
    lastName: '',
    password: '',
  };

  const submit = (values: any) =>
    registerAccount(
      {
        ...values,
        planName,
        planDuration,
        couponCode,
      },
      {
        onSuccess() {
          (window as any).fathom.trackGoal('Q73RQNEL', 0);
          removeLocalStorageCoupon();
          onSuccess();
        },
      },
    );

  useEffect(() => {
    if (searchParamCoupon && searchParamCoupon !== localStorageCoupon) {
      setLocalStorageCoupon(searchParamCoupon);
    }
  }, [searchParamCoupon, localStorageCoupon, setLocalStorageCoupon]);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={submit}
    >
      <div className="flex flex-col items-center w-full px-8 py-12 mt-8 text-center bg-white md:border sm:rounded-lg border-slate-200">
        <h1 className="mb-6 text-xl font-semibold">
          Create your StackScout account
        </h1>
        <div className="flex-col w-full pl-2 space-y-1 text-sm text-left sm:flex-row sm:justify-center sm:items-center sm:space-x-4 sm:flex text-slate-500 sm:text-center sm:w-auto sm:space-y-0 sm:pl-0">
          <div className="flex items-center space-x-1">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 512 512"
              className="w-auto h-3.5 text-[#7AD572]"
            >
              <path
                fill="currentColor"
                d="M493.3 128l-22.6 22.6-256 256-22.7 22.7-22.6-22.6-128-128L18.7 256 64 210.7l22.6 22.6L192 338.7l233.4-233.3L448 82.7l45.3 45.3z"
              ></path>
            </svg>
            <span>14 day free trial</span>
          </div>
          <div className="flex items-center space-x-1">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 512 512"
              className="w-auto h-3.5 text-[#7AD572]"
            >
              <path
                fill="currentColor"
                d="M493.3 128l-22.6 22.6-256 256-22.7 22.7-22.6-22.6-128-128L18.7 256 64 210.7l22.6 22.6L192 338.7l233.4-233.3L448 82.7l45.3 45.3z"
              ></path>
            </svg>
            <span>No credit card required</span>
          </div>
          <div className="flex items-center space-x-1">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 512 512"
              className="w-auto h-3.5 text-[#7AD572]"
            >
              <path
                fill="currentColor"
                d="M493.3 128l-22.6 22.6-256 256-22.7 22.7-22.6-22.6-128-128L18.7 256 64 210.7l22.6 22.6L192 338.7l233.4-233.3L448 82.7l45.3 45.3z"
              ></path>
            </svg>
            <span>No questions asked refund policy</span>
          </div>
        </div>

        <Form className="w-full mt-12 mb-6 space-y-4 text-left">
          <div className="u-form-field">
            <label htmlFor="email" className="u-label">
              Email address
            </label>
            <Field name="email">
              {({ field, meta: { touched, error } }) => (
                <input
                  type="email"
                  id="email"
                  autoComplete="email"
                  className={clsx('u-input', {
                    'u-input--error': touched && error,
                  })}
                  placeholder="john.crotty@example.com"
                  {...field}
                />
              )}
            </Field>
            <ErrorMessage
              name="email"
              component="div"
              className="u-validation-error"
            />
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div className="u-form-field">
              <label htmlFor="firstName" className="u-label">
                First name
              </label>
              <Field name="firstName">
                {({ field, meta: { touched, error } }) => (
                  <input
                    type="text"
                    id="firstName"
                    autoComplete="given-name"
                    className={clsx('u-input', {
                      'u-input--error': touched && error,
                    })}
                    placeholder="John"
                    {...field}
                  />
                )}
              </Field>
              <ErrorMessage
                name="firstName"
                component="div"
                className="u-validation-error"
              />
            </div>
            <div className="u-form-field">
              <label htmlFor="lastName" className="u-label">
                Last name
              </label>
              <Field name="lastName">
                {({ field, meta: { touched, error } }) => (
                  <input
                    type="text"
                    id="lastName"
                    autoComplete="family-name"
                    className={clsx('u-input', {
                      'u-input--error': touched && error,
                    })}
                    placeholder="Crotty"
                    {...field}
                  />
                )}
              </Field>
              <ErrorMessage
                name="lastName"
                component="div"
                className="u-validation-error"
              />
            </div>
          </div>

          <div className="u-form-field">
            <label htmlFor="password" className="u-label">
              Password
            </label>
            <Field name="password">
              {({ field, meta: { touched, error } }) => (
                <>
                  <input
                    type="password"
                    id="password"
                    autoComplete="new-password"
                    className={clsx('u-input', {
                      'u-input--error': touched && error,
                    })}
                    placeholder="••••••••••"
                    {...field}
                  />
                  {!touched && !error && (
                    <div className="pl-1 mt-1 text-xs text-slate-500">
                      Minimum 8 characters
                    </div>
                  )}
                </>
              )}
            </Field>
            <ErrorMessage
              name="password"
              component="div"
              className="u-validation-error"
            />
          </div>

          <button
            type="submit"
            className="w-full u-btn disabled:opacity-50"
            disabled={isSubmitting}
          >
            {isSubmitting
              ? 'Creating your StackScout account...'
              : 'Start your Free Trial'}
          </button>
        </Form>

        <p className="text-xs text-slate-500">
          By clicking this button, you agree to our{' '}
          <a
            href="/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="transition-colors text-slate-600 hover:text-primary"
          >
            Terms &amp; Conditions
          </a>{' '}
          and{' '}
          <a
            href="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="transition-colors text-slate-600 hover:text-primary"
          >
            Privacy Policy
          </a>
        </p>

        {isError && (
          <div className="w-full px-6 py-4 mt-6 text-sm text-left text-red-500 border border-red-200 rounded-sm bg-red-50">
            {error?.message}
          </div>
        )}
      </div>
    </Formik>
  );
};

export default GetStartedForm;
